import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import confetti from 'canvas-confetti';
import {
  HiOutlineCheckCircle,
  HiOutlineShieldCheck,
  HiOutlinePencilSquare,
  HiOutlineCheckBadge,
  HiOutlineDocumentText,
  HiOutlineArrowDownTray,
  HiOutlineClock,
  HiOutlineArrowPath,
} from 'react-icons/hi2';

import { useGetTransactionHistory } from 'Core/hooks/platform/useGetTransactionHistory';
import Page from 'Core/components/Page';
import { useGetNextStep } from '../hooks/useGetNextStep';
import { PrimaryButton } from 'Core/components/Button';
import { generateURL, getUrlParam } from 'Core/utils/utils';
import { useQueryClient } from 'react-query';
import cacheKeys from 'Core/hooks/queries/cacheKeys';
import { mixedDownPaymentCheckoutPath } from 'Modules/financing/routes';
import Icon from 'Core/components/Icon/Icon';
import { formatCurrency } from 'Core/utils/numberFormat';
import { features, postLogMessage, actions } from 'Core/utils/logger';
import PostExchangeBanner from 'Modules/financing/components/PostExchangeBanner';

const fireConfetti = () => {
  const colors = ['#10b981', '#34d399', '#6ee7b7', '#fde68a', '#ffffff'];
  confetti({
    particleCount: 120,
    spread: 80,
    startVelocity: 45,
    origin: { x: 0.5, y: 0.1 },
    colors,
  });
  setTimeout(() => {
    confetti({ particleCount: 60, angle: 60, spread: 55, origin: { x: 0, y: 0.3 }, colors });
    confetti({ particleCount: 60, angle: 120, spread: 55, origin: { x: 1, y: 0.3 }, colors });
  }, 250);
};

const FinancingSuccess = () => {
  document.title = 'Payment Portal - Success';
  const { t } = useTranslation();
  const routes = { mixed: mixedDownPaymentCheckoutPath };
  const { data: transactions } = useGetTransactionHistory();
  const checklistItems = [
    { label: t('page.financing.checklist.approved'), Icon: HiOutlineShieldCheck },
    { label: t('page.financing.checklist.approvedSigned'), Icon: HiOutlinePencilSquare },
    { label: t('page.financing.checklist.funded'), Icon: HiOutlineCheckBadge },
  ];
  const [isCollectPaymentLoading, setIsCollectPaymentLoading] = React.useState(false);
  const [refetchInterval, setRefetchInterval] = React.useState(false);
  const {
    data: application,
    isSuccess: isGetNextStepSuccess,
    refetch,
    isFetching,
  } = useGetNextStep(refetchInterval);
  const {
    next_action,
    external_reference_id = '',
    payload: { financing_down_payment = 0, payment_id = '', application_id = '' } = {},
  } = application;
  React.useEffect(() => {
    if (isGetNextStepSuccess && payment_id && application_id && external_reference_id) {
      postLogMessage({
        feature: features.financingSignContract,
        action: actions.financingSuccessScreenDisplayed,
        payment_id,
        application_id,
        external_reference_id,
      });
    }
  }, [isGetNextStepSuccess]);
  const queryClient = useQueryClient();
  const confettiFiredRef = React.useRef(false);

  const onCollectPayment = () => {
    const payId = getUrlParam('payment-id');
    const web_code = getUrlParam('web-code');
    const url = generateURL({
      url: routes.mixed,
      params: { 'payment-id': payId, 'web-code': web_code },
    });
    setIsCollectPaymentLoading(true);
    setRefetchInterval(5000);
    queryClient.invalidateQueries(cacheKeys.financingNextStep);

    setTimeout(() => setIsCollectPaymentLoading(false), 5 * 60 * 1000);
    window.open(url, '_blank')?.focus();
  };

  React.useEffect(() => {
    if (next_action === 'DONE') {
      setRefetchInterval(false);
      queryClient.invalidateQueries(cacheKeys.transactionHistory);
    }
  }, [next_action, queryClient]);

  React.useEffect(() => {
    if (confettiFiredRef.current) return;
    if (typeof window === 'undefined') return;
    confettiFiredRef.current = true;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;
    fireConfetti();
  }, []);

  return (
    <Page title={t('page.financing.header')} withStepProgress={false} stickyFooter>
      <div className="mx-auto flex max-w-4xl flex-col gap-5 px-4 pb-10 pt-5 text-slate-900">
        <PostExchangeBanner application={application} />
        <section className="rounded-2xl border border-slate-200 bg-white px-5 py-3 shadow-sm sm:px-7">
          <div className="flex items-start justify-between gap-4">
            <div className="relative flex h-16 w-16 shrink-0 items-center justify-center">
              <span
                className="absolute inset-0 rounded-full bg-green-400/20 blur-md"
                aria-hidden="true"
              />
              <span
                className="absolute inset-1.5 rounded-full border-2 border-green-500/50 animate-ping motion-reduce:animate-none"
                aria-hidden="true"
              />
              <span className="relative z-10 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-600">
                <HiOutlineCheckCircle className="h-7 w-7" />
              </span>
            </div>

            <button
              type="button"
              onClick={() => refetch()}
              disabled={isFetching}
              title="Refresh status"
              data-testid="refresh-status-button"
              className="inline-flex items-center gap-2 rounded-lg border border-solid border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-600 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:opacity-50"
            >
              <HiOutlineArrowPath className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          <div className="flex flex-col items-center gap-2 pb-3 pt-2 text-center">
            <h1 className="m-0 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
              {t('page.financing.financingFunded.infobox')}
            </h1>
            <p className="mx-auto mb-0 max-w-xl text-sm leading-relaxed text-slate-500">
              {t('page.financing.financingFunded.intro')}
            </p>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white px-6 py-6 shadow-sm sm:px-7">
          <div className="mx-auto flex w-fit flex-col gap-1">
            {checklistItems.map(({ label, Icon: StepIcon }) => (
              <div
                key={label}
                data-testid="checklist-item-done"
                className="flex items-center gap-3.5 py-2"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-600">
                  <StepIcon className="h-[18px] w-[18px]" />
                </span>
                <span className="text-base text-slate-900">{label}</span>
              </div>
            ))}
          </div>

          {transactions && transactions.length > 0 && (
            <>
              <hr className="my-5 border-0 border-t border-slate-200" />
              <h4 className="m-0 mb-3.5 text-base font-bold tracking-tight text-slate-900">
                Documents
              </h4>
              <div className="flex flex-wrap gap-3.5">
                {transactions.map(
                  ({ payment_id: paymentId, receipt: { url = '' } = {} }, idx, arr) => {
                    return (
                      <a
                        key={paymentId}
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={'receipt-download-' + idx}
                        className="group flex max-w-sm flex-1 items-center gap-3.5 rounded-xl border border-slate-200 bg-gradient-to-b from-slate-50 to-slate-100/60 px-4 py-3.5 text-slate-900 no-underline transition [flex-basis:260px] hover:-translate-y-px hover:border-green-400 hover:from-green-50 hover:to-green-100/60 hover:shadow-md"
                      >
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-green-100 text-green-600">
                          <HiOutlineDocumentText className="h-5 w-5" />
                        </span>
                        <span className="flex-1 text-sm font-semibold text-slate-900">
                          Down Payment Receipt {arr.length > 1 ? idx + 1 : ''}
                        </span>
                        <span className="shrink-0 text-slate-400 transition-colors group-hover:text-green-600">
                          <HiOutlineArrowDownTray className="h-[18px] w-[18px]" />
                        </span>
                      </a>
                    );
                  }
                )}
              </div>
            </>
          )}
        </section>

        {next_action === 'PENDING_DOWN_PAYMENT' && (
          <section
            data-testid="correction-required-message"
            className="rounded-xl border border-amber-300 bg-amber-50 px-5 py-4"
          >
            <div className="mb-2.5 flex items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                <HiOutlineClock className="h-[18px] w-[18px]" />
              </span>
              <span className="text-xs font-bold uppercase tracking-wider text-amber-700">
                correction required
              </span>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-4 text-[0.95rem] leading-snug text-amber-900">
              <span className="flex-1 [flex-basis:280px]">
                <Trans
                  i18nKey="financing.downPayment.outstandingMessage"
                  values={{
                    amount: formatCurrency(financing_down_payment, { withDecimals: false }),
                  }}
                />
              </span>
              <span className="min-w-[160px] shrink-0">
                <PrimaryButton
                  data-testid="btn-verify"
                  block
                  onClick={onCollectPayment}
                  disabled={isCollectPaymentLoading}
                >
                  {isCollectPaymentLoading && <Icon type="spinner" className="mr-4"></Icon>}
                  Collect Payment
                </PrimaryButton>
              </span>
            </div>
          </section>
        )}
      </div>
    </Page>
  );
};

export default FinancingSuccess;
